import React, { useState, useEffect } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Row, Col, ButtonGroup, Button, Card, CardBody, CardTitle, CardText } from 'reactstrap';
import { Form, FormGroup, Label, InputGroup, InputGroupAddon } from 'reactstrap';
import { Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import { Field, reduxForm } from 'redux-form';
import { Task } from '../../../models';
import { loadShift, deleteShift, createShift, assignTask, changeTaskStatus } from '../../../redux';
import { Loader, DateTimePicker, SectorField, ActivitiesField, UsersField } from '../../../components';
import classnames from 'classnames';
import { buildShiftDuplicatePayload } from '../shiftDuplicate';

import user_avatar from '../../../assets/images/user_avatar.webp';
import { TaskUserLabel } from '../../../containers/Tasks/taskUserDisplay';

import './ShiftForm.css';

let ShiftForm = (props) => {
    const {
        currentShiftId, currentPreviewUserId, sectors, activities, users,
        onRemove, onShowSuggestion, onDuplicated, applicationTypeId,
        draftShift,
    } = props;
    const { handleSubmit, pristine, submitting } = props;

    const { t } = useTranslation();
    const dispatch    = useDispatch();

    const [panelTab, setPanelTab]                       = useState('collaborators');
    const [activeTab, setActiveTab]                       = useState('available');
    const [confirmationIsVisible, setConfirmationVisible] = useState(false);

    const { isLoadingOne, shift } = useSelector(state => state.shifts);
    const isDraft = !!draftShift?.isDraft;
    const saveDisabled = (!isDraft && pristine) || submitting || isLoadingOne;

    const formUsers = React.useMemo(() => {
        const linked_users = users ?? [];
        const shift_admins = shift?.admins ?? [];
        const by_id = new Map(linked_users.map((user) => [user.id, user]));
        shift_admins.forEach((user) => {
            if (user?.id && !by_id.has(user.id))
                by_id.set(user.id, user);
        });
        return Array.from(by_id.values());
    }, [users, shift?.admins]);

    const sectorAdminsFallback = shift?.sector?.admins || [];
    const adminIds = useSelector(state => state.form?.shiftForm?.values?.admin_ids);
    const showSectorAdminsFallback = !(adminIds?.length) && sectorAdminsFallback?.length > 0;

    const suggestions = React.useMemo(() => (shift?.suggestions || []).filter(
        suggestion => !!suggestion.in_sector && !suggestion.overbooked
    ), [ shift ]);

    const outOfSectorSuggestions = React.useMemo(() => (shift?.suggestions || []).filter(
        suggestion => !suggestion.in_sector && !suggestion.overbooked
    ), [ shift ]);

    const overbookedSuggestions = React.useMemo(() => (shift?.suggestions || []).filter(
        suggestion => !!suggestion.overbooked
    ), [ shift ]);

    useEffect(() => {
        if (suggestions?.length)
            setActiveTab('available');
        else if (outOfSectorSuggestions?.length)
            setActiveTab('out_of_sector');
        else if (overbookedSuggestions?.length)
            setActiveTab('overbooked');
    }, [outOfSectorSuggestions.length, overbookedSuggestions.length, shift, suggestions.length]);

    const toggle = tab => {
        if (activeTab !== tab)
            setActiveTab(tab);
    }

    const remove = () => {
        dispatch(deleteShift(shift?.id, (err) => {
            onRemove && onRemove(err);
        }));
    };

    const duplicate = () => {
        const payload = buildShiftDuplicatePayload(shift, {}, applicationTypeId);
        if (!payload)
            return;

        dispatch(createShift(payload, null, (err, created) => {
            if (!err && created?.id && onDuplicated)
                onDuplicated(created);
        }));
    };

    const removeVolunteer = (e, task_id) => {
        e.stopPropagation();

        dispatch(changeTaskStatus(task_id, 'free', (_err, _task) => {
            dispatch(loadShift(shift?.id));
        }));
    };

    const addVolunteer = (e, userId) => {
        e.stopPropagation();

        const assignable = shift?.tasks.find(task => !task.user_id);
        if (assignable)
            dispatch(assignTask(assignable.id, userId, (_err, _task) => {
                dispatch(loadShift(shift?.id));
            }));
    };

    const renderSuggestions = (suggestions, assignable) => {
        return suggestions?.map(suggestion => (
            <Row key={ suggestion.id } className="my-1" onClick={() => onShowSuggestion(suggestion.id)}>
                <Col sm={12}>
                    <div className="volunteer_row" sm={12}>
                        <div className="img_wrapper">
                            <img src={ suggestion.avatar || user_avatar } alt={suggestion.fullname || 'Volunteer avatar'} />
                        </div>
                        <div className="infos_wrapper">
                            <div className="name">
                                <Link target="_blank" rel="noopener noreferrer" to={`/users/edit/${ suggestion.id }/activities`}>
                                    { suggestion.fullname }
                                </Link>
                            </div>
                        </div>
                        <ButtonGroup>
                            <Button color={currentPreviewUserId === suggestion.id ? 'danger' : 'info'} onClick={() => onShowSuggestion(suggestion.id) }>
                                <i className={`fa fa-${currentPreviewUserId === suggestion.id ? 'eye-slash' : 'eye'}`} />
                            </Button>
                            {assignable && (
                                <Button color="success" onClick={e => addVolunteer(e, suggestion.id) }>
                                    <i className="fa fa-plus" />
                                </Button>
                            )}
                        </ButtonGroup>
                    </div>
                </Col>
            </Row>
        ));
    };

    return (
        <Form className="ShiftForm" onSubmit={handleSubmit}>
            {isLoadingOne || (!isDraft && currentShiftId && (currentShiftId !== shift?.id)) ? (
                <Card>
                    <CardBody>
                        <Loader />
                    </CardBody>
                </Card>
            ) : (
                <Row className="my-0">
                    <Col>
                        <Card>
                            <CardBody>
                                <CardTitle className="small">
                                    <i className="fa fa-map-marker"></i>&nbsp;
                                    {t("shifts.sector")}
                                </CardTitle>
                                <CardText tag="div" className="ShiftForm__section-body">
                                    <FormGroup row className="mb-0">
                                        <Col sm={12}>
                                            <SectorField name="sector_id" sectors={ sectors } ensure={ shift?.sector } single />
                                        </Col>
                                    </FormGroup>
                                </CardText>
                                <CardTitle className="small">
                                    <i className="fa fa-calendar"></i>&nbsp;
                                    {t("shifts.dates")}
                                </CardTitle>
                                <CardText tag="div" className="ShiftForm__section-body">
                                    <FormGroup row className="mb-2">
                                        <Label for="start_at" sm={3}>{t('shifts.start_at')}</Label>
                                        <Col sm={9}>
                                            <Field
                                                component={DateTimePicker}
                                                type="text"
                                                name="start_at"
                                                maxBoundField="stop_at"
                                            />
                                        </Col>
                                    </FormGroup>
                                    <FormGroup row className="mb-0">
                                        <Label for="stop_at" sm={3}>{t('shifts.stop_at')}</Label>
                                        <Col sm={9}>
                                            <Field
                                                component={DateTimePicker}
                                                type="text"
                                                name="stop_at"
                                                endOfDayIfMidnight
                                                minBoundField="start_at"
                                            />
                                        </Col>
                                    </FormGroup>
                                </CardText>
                                {activities?.length > 1 && (
                                    <>
                                        <CardTitle className="small">
                                            <i className="fa fa-id-badge"></i>&nbsp;
                                            {t("shifts.activity")}
                                        </CardTitle>
                                        <CardText tag="div" className="ShiftForm__section-body">
                                            <FormGroup row className="mb-0">
                                                <Col sm={12}>
                                                    <ActivitiesField single name="activity_id" activities={ activities || [] } />
                                                </Col>
                                            </FormGroup>
                                        </CardText>
                                    </>
                                )}
                                <CardTitle className="small">
                                    <i className="fa fa-tasks"></i>&nbsp;
                                    {t("shifts.status")}
                                </CardTitle>
                                <CardText tag="div" className="ShiftForm__section-body">
                                    <FormGroup row className="mb-0">
                                        <Col sm={12}>
                                            <Field component="select" className="form-control" name="status" id="status">
                                                <option value={Task.STATUS_NEW}>{ t('shifts.status_new') }</option>
                                                <option value={Task.STATUS_FREE}>{ t('shifts.status_free') }</option>
                                            </Field>
                                        </Col>
                                    </FormGroup>
                                </CardText>

                                <div className="ShiftForm__actions">
                                    <Button
                                        type="submit"
                                        color="success"
                                        block
                                        className="ShiftForm__action-save"
                                        disabled={saveDisabled}
                                    >
                                        {isLoadingOne ?
                                            <span>
                                                &nbsp;<i className="fa fa-spinner fa-spin"></i>
                                            </span>
                                            :
                                            <span>
                                                <i className="fa fa-save"></i>&nbsp;
                                                {t("common.save")}
                                            </span>
                                        }
                                    </Button>
                                    {!isDraft && (
                                    <div className="ShiftForm__secondary-actions">
                                        <ButtonGroup size="sm">
                                            <Button
                                                type="button"
                                                color="warning"
                                                disabled={submitting || isLoadingOne || !shift?.id}
                                                onClick={duplicate}
                                                title={t('plan.duplicate_alt_drag')}
                                            >
                                                <span>
                                                    <i className="fa fa-copy"></i>&nbsp;
                                                    {t('common.duplicate')}
                                                </span>
                                            </Button>
                                            {confirmationIsVisible ? (
                                                <div className="remove-shift-wrapper">
                                                    <span className="remove-shift-wrapper__label">
                                                        {t('common.are_you_sure')}
                                                    </span>
                                                    <ButtonGroup size="sm">
                                                        <Button
                                                            type="button"
                                                            onClick={() => setConfirmationVisible(false)}
                                                            color="info"
                                                        >
                                                            <i className="fa fa-times"></i> {t("common.no")}
                                                        </Button>
                                                        <Button type="button" color="danger" onClick={remove}>
                                                            <span>
                                                                <i className="fa fa-trash"></i>&nbsp;{t("common.yes")}
                                                            </span>
                                                        </Button>
                                                    </ButtonGroup>
                                                </div>
                                            ) : (
                                                <Button
                                                    type="button"
                                                    color="danger"
                                                    onClick={() => setConfirmationVisible(true)}
                                                >
                                                    <span>
                                                        <i className="fa fa-trash"></i>&nbsp;{t("common.delete")}
                                                    </span>
                                                </Button>
                                            )}
                                        </ButtonGroup>
                                    </div>
                                    )}
                                </div>

                                <Nav tabs className="ShiftForm__panel-tabs">
                                    <NavItem>
                                        <NavLink
                                            className={classnames({ active: panelTab === 'collaborators' })}
                                            onClick={() => setPanelTab('collaborators')}
                                        >
                                            <i className="fa fa-users"></i>&nbsp;
                                            {t('shifts.needs')}
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink
                                            className={classnames({ active: panelTab === 'admins' })}
                                            onClick={() => setPanelTab('admins')}
                                        >
                                            <i className="fa fa-user-tie"></i>&nbsp;
                                            {t('shifts.tab_admins')}
                                        </NavLink>
                                    </NavItem>
                                </Nav>
                                <TabContent activeTab={panelTab} className="ShiftForm__panel-tab-content">
                                    <TabPane tabId="collaborators">
                                        <FormGroup row className="mb-2 mt-2">
                                            <Label for="nb_volunteers" sm={3}>{t('shifts.nb_volunteers')}</Label>
                                            <Col sm={9}>
                                                <InputGroup>
                                                    <Field
                                                        component="input"
                                                        type="number"
                                                        name="nb_volunteers"
                                                        id="nb_volunteers"
                                                        className="form-control shift-form-nb-collaborators"
                                                        min={ 0 }
                                                        max={ 100 }
                                                        onFocus={(e) => e.target.select()}
                                                    />
                                                    <InputGroupAddon addonType="append">
                                                        <Button
                                                            type="button"
                                                            color="success"
                                                            disabled={saveDisabled}
                                                            onClick={handleSubmit}
                                                            title={t('common.save')}
                                                        >
                                                            {isLoadingOne || submitting ? (
                                                                <i className="fa fa-spinner fa-spin" />
                                                            ) : (
                                                                <i className="fa fa-save" />
                                                            )}
                                                        </Button>
                                                    </InputGroupAddon>
                                                </InputGroup>
                                            </Col>
                                        </FormGroup>

                                        {(shift?.nb_assigned > 0) && (
                                            <>
                                                <CardTitle className="small">
                                                    {t("shifts.assigned")} - { shift.nb_assigned } / { shift.nb_volunteers }
                                                </CardTitle>
                                                <CardText tag="div" className="mb-3">
                                                    {shift.tasks?.filter(task => !!task.user_id).sort((a, b) => a.user?.fullname.localeCompare(b.user?.fullname)).map(task => (
                                                        <Row key={ task.id } className="my-1" onClick={ () => onShowSuggestion(task.user_id) }>
                                                            <Col sm={12}>
                                                                <div className="volunteer_row" sm={12}>
                                                                    <div className="img_wrapper">
                                                                        <img src={task.user?.avatar || user_avatar} alt={task.user?.fullname || 'Assigned volunteer avatar'} />
                                                                    </div>
                                                                    <div className="infos_wrapper">
                                                                        <div className="name">
                                                                            {task.user?.fullname ? (
                                                                                task.user.former_member ? (
                                                                                    <TaskUserLabel user={task.user} t={t} />
                                                                                ) : (
                                                                                    <Link target="_blank" rel="noopener noreferrer" to={`/users/edit/${ task.user_id }/activities`}>
                                                                                        {task.user.fullname}
                                                                                    </Link>
                                                                                )
                                                                            ) : (
                                                                                <i>{t('shifts.user_can_not_be_found')}</i>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    <div className="action">
                                                                        <ButtonGroup>
                                                                            <Button color={currentPreviewUserId === task.user_id ? 'danger' : 'info'} onClick={() => onShowSuggestion(task.user_id) }>
                                                                                <i className={`fa fa-${currentPreviewUserId === task.user_id ? 'eye-slash' : 'eye'}`} />
                                                                            </Button>
                                                                            <Button color="danger" onClick={e => removeVolunteer(e, task.id) }>
                                                                                <i className="fa fa-times" />
                                                                            </Button>
                                                                        </ButtonGroup>
                                                                    </div>
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    ))}
                                                </CardText>
                                            </>
                                        )}
                                        {(shift?.nb_assigned < shift?.tasks?.length) && (
                                            <>
                                                <CardTitle className="small">
                                                    {t("shifts.suggestions")}
                                                </CardTitle>
                                                <CardText tag="div">
                                                    {(shift.suggestions?.length === 0) ? (
                                                    <span>
                                                        { t('shifts.no_suggestions_found') }
                                                    </span>
                                                    ) : (
                                                        <>
                                                            <Nav tabs justified className="suggestions-tabs">
                                                                <NavItem>
                                                                    <NavLink
                                                                        className={classnames({ active: activeTab === 'available' })}
                                                                        onClick={() => { toggle('available'); }}
                                                                    >
                                                                        {t('shifts.available')} ({ suggestions?.length })
                                                                    </NavLink>
                                                                </NavItem>
                                                                <NavItem>
                                                                    <NavLink
                                                                        className={classnames({ active: activeTab === 'out_of_sector' })}
                                                                        onClick={() => { toggle('out_of_sector'); }}
                                                                    >
                                                                        {t('shifts.out_of_sector')} ({ outOfSectorSuggestions.length })
                                                                    </NavLink>
                                                                </NavItem>
                                                                <NavItem>
                                                                    <NavLink
                                                                        className={classnames({ active: activeTab === 'overbooked' })}
                                                                        onClick={() => { toggle('overbooked'); }}
                                                                    >
                                                                        {t('shifts.overbooked')} ({ overbookedSuggestions.length })
                                                                    </NavLink>
                                                                </NavItem>
                                                            </Nav>
                                                            <TabContent activeTab={activeTab}>
                                                                <TabPane tabId="available">
                                                                    { renderSuggestions(suggestions, /*assignable*/true) }
                                                                </TabPane>
                                                                <TabPane tabId="out_of_sector">
                                                                    { renderSuggestions(outOfSectorSuggestions, /*assignable*/true) }
                                                                </TabPane>
                                                                <TabPane tabId="overbooked">
                                                                    { renderSuggestions(overbookedSuggestions, /*assignable*/false) }
                                                                </TabPane>
                                                            </TabContent>
                                                        </>
                                                    )}
                                                </CardText>
                                            </>
                                        )}
                                    </TabPane>
                                    <TabPane tabId="admins">
                                        <div className="ShiftForm__admins-help text-muted small mt-2 mb-2">
                                            <p className="mb-0">{t('shifts.admins_help_purpose')}</p>
                                        </div>
                                        <FormGroup row className="mb-0">
                                            <Col sm={12}>
                                                <UsersField
                                                    key={formUsers.map((user) => user.id).join(',')}
                                                    name="admin_ids"
                                                    className="form-control"
                                                    users={formUsers}
                                                />
                                            </Col>
                                        </FormGroup>
                                        {showSectorAdminsFallback && (
                                            <div className="text-muted small mt-1">
                                                {t('shifts.admins_sector_fallback')}:{' '}
                                                {sectorAdminsFallback.map((admin) => admin.fullname).filter(Boolean).join(', ')}
                                            </div>
                                        )}
                                        <div className="ShiftForm__admins-help text-muted small mt-3 mb-0">
                                            <p className="mb-0">{t('shifts.admins_help_escalation')}</p>
                                        </div>
                                    </TabPane>
                                </TabContent>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            )}
        </Form>
    );

};

ShiftForm = reduxForm({
  form: 'shiftForm',
  enableReinitialize: true
})(ShiftForm);

ShiftForm = connect(
  (state, ownProps) => ({
    initialValues: ownProps.draftShift || state.shifts.shift,
    Shifts: state.shifts,
}), {}
)(ShiftForm)

export default ShiftForm;
